import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOnboardingProgress, updateOnboardingProgress } from "../../api";
import { brandSettings } from "../../modules/settings/BrandSettings";
import { useAppContext } from "../../modules/apps/AppContext";

// Mock icon component for demo
const KTIcon = ({ iconName, className }: { iconName: string; className: string }) => (
  <i className={`bi ${getBootstrapIcon(iconName)} ${className}`}></i>
);

const getBootstrapIcon = (iconName: string): string => {
  const iconMap: { [key: string]: string } = {
    'questionnaire-tablet': 'bi-clipboard-check',
    'cross': 'bi-x',
    'profile-circle': 'bi-person-circle',
    'package': 'bi-box',
    'people': 'bi-people',
    'dollar': 'bi-currency-dollar',
    'chart-line-up': 'bi-graph-up',
    'file-up': 'bi-file-earmark-arrow-up',
    'check-circle': 'bi-check-circle-fill',
    'magnifier': 'bi-search',
    'notification': 'bi-bell',
    'send': 'bi-envelope'
  };
  return iconMap[iconName] || 'bi-circle';
};

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  iconName: string;
  completed: boolean;
  path: string;
  order: number;
}

interface OnboardingProgress {
  userId: string;
  bizId: string;
  steps: { [key: string]: boolean };
  isDismissed: boolean;
  lastUpdated: Date;
  completedAt?: Date;
}

interface OnboardingHelperProps {
  className?: string;
}

// Mock global value for demo
const globalValue = {
  user: { userId: 'demo-user' },
  bizMaster: { bizId: 'demo-biz' }
};

const defaultSteps: OnboardingStep[] = [
  {
    id: 'business-profile',
    title: 'Setup Business Profile',
    description: 'Complete your business information, add logo, and configure settings',
    iconName: 'profile-circle',
    completed: false,
    path: '/settings/business-profile',
    order: 1
  },
  {
    id: 'create-item',
    title: 'Create Item (Product or Service)',
    description: 'Add your first product or service to start invoicing',
    iconName: 'package',
    completed: false,
    path: '/items/create-item',
    order: 2
  },
  {
    id: 'create-customer',
    title: 'Create Customer',
    description: 'Add your first customer to manage relationships',
    iconName: 'people',
    completed: false,
    path: '/contacts/create-contact',
    order: 3
  },
  {
    id: 'create-invoice',
    title: 'Create Invoice',
    description: 'Generate your first invoice and send it to customers',
    iconName: 'dollar',
    completed: false,
    path: '/transactions/create-invoice',
    order: 4
  },
  {
    id: 'check-reports',
    title: 'Check Reports',
    description: 'Explore financial reports and business insights',
    iconName: 'chart-line-up',
    completed: false,
    path: '/reports/report-menu',
    order: 5
  },
  {
    id: 'import-data',
    title: 'Import Data',
    description: 'Import existing data from spreadsheets or other systems',
    iconName: 'file-up',
    completed: false,
    path: '/settings/data-upload',
    order: 6
  },
  {
    id: 'toolbar-search',
    title: 'Search from Toolbar',
    description: 'Use the search icon in the top toolbar to quickly find transactions, contacts, and more',
    iconName: 'magnifier',
    completed: false,
    path: '/dashboard',
    order: 7
  },
  {
    id: 'overdue-reminders',
    title: 'Set Up Overdue Reminders',
    description: 'Automatically send payment reminder emails to customers with overdue invoices',
    iconName: 'notification',
    completed: false,
    path: '/settings/overdue-reminder',
    order: 8
  },
  {
    id: 'business-summary-email',
    title: 'Business Summary Email',
    description: 'Schedule automatic business summary reports to be sent to your email',
    iconName: 'send',
    completed: false,
    path: '/settings/business-summary',
    order: 9
  }
];


export const OnboardingHelper: React.FC<OnboardingHelperProps> = ({ className = '' }) => {
  const { roleAccess } = useAppContext();
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState<OnboardingStep[]>(defaultSteps);

  const navigate = useNavigate();

  const loadProgress = useCallback(async () => {
    try {
      setLoading(true);
      const progressData = await getOnboardingProgress(); // Remove mockApi here
      setProgress(progressData);
      // Only show if enabled and not dismissed
      setIsVisible(progressData.isDismissed !== true);

      // Update steps with completion status
      const updatedSteps = defaultSteps.map(step => ({
        ...step,
        completed: (progressData.steps as any)[step.id] || false
      }));
      setSteps(updatedSteps);

    } catch (error) {
      console.error('Error loading onboarding progress:', error);
      // Show component by default if API fails
      setIsVisible(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const handleStepToggle = async (stepId: string) => {
    if (!progress) return;

    try {
      // Type-safe way to access the step
      const currentCompleted = (progress.steps as any)[stepId] || false;
      const newCompleted = !currentCompleted;

      // Create updated steps object with proper typing
      const updatedSteps = {
        ...progress.steps,
        [stepId]: newCompleted
      } as OnboardingProgress['steps'];

      // Update using the real API
      await updateOnboardingProgress({
        steps: updatedSteps
      });

      // Reload progress
      await loadProgress();
    } catch (error) {
      console.error('Error updating step:', error);
    }
  };

  const handleDismiss = async () => {
    try {
      await updateOnboardingProgress({ isDismissed: true });
      setIsVisible(false);
    } catch (error) {
      console.error('Error dismissing onboarding:', error);
      // Still hide the component locally
      setIsVisible(false);
    }
  };

  const handleStepClick = (step: OnboardingStep) => {
    navigate(step.path);
  };

  const completedSteps = steps.filter(step => step.completed).length;
  const totalSteps = steps.length;
  const progressPercentage = (completedSteps / totalSteps) * 100;

  // Only show for admin users
  if (!roleAccess?.isAdmin) {
    return null;
  }

  // Don't render if loading or not visible
  if (loading || !isVisible) {
    return null;
  }

  return (
    <div className={`card ${className}`}>
      {/* Header */}
      <div className="card-header border-0 pt-6">
        <div className="card-title align-items-start flex-column">
          <div className="d-flex align-items-center mb-2">
            <div className="symbol symbol-40px symbol-circle me-3">
              <div className="symbol-label bg-light-primary">
                <KTIcon iconName="questionnaire-tablet" className="fs-2 text-primary" />
              </div>
            </div>
            <div>
              <span className="card-label fw-bold fs-3 mb-1">{'Welcome to'.toTranslatedDisplay()} {brandSettings.displayName}</span>
              <div className="text-muted fw-semibold fs-7">
                {'Complete these steps to get the most out of your account'.toTranslatedDisplay()}
              </div>
            </div>
          </div>
        </div>

        <div className="card-toolbar">
          <button
            type="button"
            className="btn btn-sm btn-icon btn-color-gray-500 btn-active-light-primary"
            onClick={handleDismiss}
            title={'Dismiss onboarding'.toTranslatedDisplay()}
          >
            <KTIcon iconName="cross" className="fs-2" />
          </button>
        </div>
      </div>

      <div className="card-body pt-0">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="fw-semibold fs-6 text-gray-800">
              {'Progress'.toTranslatedDisplay()}: {completedSteps} {'of'.toTranslatedDisplay()} {totalSteps} {'completed'.toTranslatedDisplay()}
            </span>
            <span className="badge badge-light-primary fs-7">
              {Math.round(progressPercentage)}%
            </span>
          </div>

          <div className="progress h-8px bg-light-primary">
            <div
              className="progress-bar bg-primary"
              role="progressbar"
              style={{ width: `${progressPercentage}%` }}
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>

        {/* Steps Grid */}
        <div className="row g-4">
          {steps.map((step) => {
            const isCompleted = step.completed;

            return (
              <div key={step.id} className="col-lg-4 col-md-6">
                <div
                  className={`card h-100 border cursor-pointer ${isCompleted
                    ? 'border-success bg-light-success'
                    : 'border-gray-300 card-hover'
                    }`}
                  onClick={() => handleStepClick(step)}
                >
                  <div className="card-body p-6">
                    <div className="d-flex align-items-start">
                      {/* Checkbox */}
                      <div
                        className={`form-check form-check-custom form-check-solid me-4 ${isCompleted ? 'form-check-success' : ''
                          }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStepToggle(step.id);
                        }}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={isCompleted}
                          readOnly
                        />
                      </div>

                      <div className="flex-grow-1">
                        {/* Icon and Title */}
                        <div className="d-flex align-items-center mb-3">
                          <div className={`symbol symbol-30px me-3 ${isCompleted ? 'symbol-success' : 'symbol-primary'
                            }`}>
                            <div className="symbol-label">
                              <KTIcon
                                iconName={step.iconName}
                                className={`fs-4 ${isCompleted ? 'text-success' : 'text-primary'
                                  }`}
                              />
                            </div>
                          </div>
                          <h5 className={`mb-0 ${isCompleted ? 'text-success' : 'text-gray-900'
                            }`}>
                            {step.title.toTranslatedDisplay()}
                          </h5>
                        </div>

                        {/* Description */}
                        <p className={`fs-7 m-0 ${isCompleted ? 'text-success' : 'text-muted'
                          }`}>
                          {step.description.toTranslatedDisplay()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Completion Message */}
        {completedSteps === totalSteps && (
          <div className="alert alert-success d-flex align-items-center mt-8">
            <KTIcon iconName="check-circle" className="fs-2 text-success me-4" />
            <div>
              <h4 className="alert-heading fw-bold mb-1">{'Congratulations!'.toTranslatedDisplay()}</h4>
              <p className="mb-0">
                {'You\'ve completed all onboarding steps. You\'re ready to make the most of'.toTranslatedDisplay()} {brandSettings.displayName}!
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="d-flex justify-content-between align-items-center mt-8 pt-6 border-top border-gray-200">
          <span className="text-muted fs-7">
            {'Need help?'.toTranslatedDisplay()} <a href="/help" className="link-primary">{'Contact our support team'.toTranslatedDisplay()}</a>
          </span>
          <button
            type="button"
            className="btn btn-sm btn-light-primary"
            onClick={handleDismiss}
          >
            {'Dismiss this guide'.toTranslatedDisplay()}
          </button>
        </div>
      </div>
    </div>
  );
};